import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef, HostBinding,
    Input,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';

import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PopoverController } from 'ionic-angular';
import { SplatToolsComponent } from './splat-control-tools.component';
import { Subject } from 'rxjs/Subject';
import { MessageBusService } from '../../../../core/services/message-bus/message-bus.service';

import Hammer from 'hammerjs';
import { FormPage } from '../../../pages/form/form';
import { ControlComponent } from '../../control.component';
import { ControlSplat } from '../splat-control.model';
import { SplatModel } from './lib/splat-model.model';
import { SplatRenderer } from './lib/splat-renderer.model';
import { SplatImageMap } from './lib/splat-image-map.model';
import { SplatObject } from './lib/splat-object.model';
import { SplatState } from './lib/splat-state.interface';
import { Point } from './lib/point.interface';
import { AppConfig } from '../../../../app/app.config';
import { SplatControlConfigModelIconSizeEnum } from '../splat-control-config.model';

@Component({
    selector: 'splat',
    template: `
        <ion-toolbar>
            <ion-row>
                <ion-col col-10>
                    <ion-segment [(ngModel)]="mode">
                        <ion-segment-button value="add">
                            <ion-icon name="add"></ion-icon>
                        </ion-segment-button>
                        <ion-segment-button value="remove">
                            <ion-icon name="remove"></ion-icon>
                        </ion-segment-button>
                        <ion-segment-button value="move">
                            <ion-icon name="move"></ion-icon>
                        </ion-segment-button>
                        <ion-segment-button value="rotate">
                            <ion-icon name="refresh"></ion-icon>
                        </ion-segment-button>
                    </ion-segment>
                </ion-col>
                <ion-col col-2>
                    <button
                        (click)="showTools($event)"
                        ion-button>
                        <ion-icon name="more"></ion-icon>
                    </button>
                </ion-col>
            </ion-row>
        </ion-toolbar>
        <div text-center>
            <canvas
                #canvas
                (tap)="onTap($event)"
                (press)="onPress($event)"
            ></canvas>
        </div>
    `,
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SplatControlComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => SplatControlComponent), multi: true}
    ]
})
export class SplatControlComponent extends ControlComponent implements ControlValueAccessor, OnDestroy, AfterViewInit {
    @ViewChild('canvas') canvasRef: ElementRef;
    @Input() definition: ControlSplat;
    @Input() formControl: FormControl;
    @Output() changes: EventEmitter<any> = new EventEmitter();
    @Input('value') _value: any = undefined;

    protected mode: string = 'add';
    protected icon: string = 'cross-red';
    protected image: string = 'car';
    protected ngUnsubscribe: Subject<void> = new Subject<void>();
    protected canvas: any;

    protected splatModel: SplatModel;
    protected splatRenderer: SplatRenderer;
    protected splatImageMap: SplatImageMap;
    protected selectedList: Array<SplatObject> = [];

    constructor(
        protected elemRef: ElementRef,
        protected popoverCtrl: PopoverController,
        protected messageBusService: MessageBusService
    ) {
        super(elemRef);
    }

    ngAfterViewInit(): void {
        this.canvas = this.canvasRef.nativeElement;
        this.canvas.width = 400;
        this.canvas.height = 200;

        this.icon = this.definition.config.iconSize
        === SplatControlConfigModelIconSizeEnum.LARGE
            ? 'cross-red'
            : 'cross-red-small';

        this.splatImageMap = this.getSplatImageMap();
        this.splatModel = new SplatModel(this.splatImageMap);
        this.splatRenderer = new SplatRenderer(this.canvas, this.splatModel, this.splatImageMap);

        this.splatRenderer.onReady(() => {

            if (!this.value) {
                this.setImage(this.image);
            } else {
                this.splatModel.deserialise(this.value);
            }

            this.splatModel.state$.takeUntil(this.ngUnsubscribe).subscribe((state: SplatState) => {
                this.updateValue(this.splatModel.serialise());
            });
        });

        const gesture: any = new Hammer(this.canvas);
        gesture.get('pan').set({direction: Hammer.DIRECTION_ALL});

        // gesture.on("panstart", e => {});

        gesture.on('panmove', e => {
            this.onPan(e);
        });

        gesture.on('panend', e => {
            this.clearSelected();
            this.messageBusService.send(FormPage.CLASS, 'unlockSlides');
        });
    }

    addSplat(center: Point): void {
        this.splatModel.addSplatObject({
            imageKey: this.icon,
            center
        });
    }

    clearSelected(): void {

        this.messageBusService.send(FormPage.CLASS, 'unlockSlides');

        this.selectedList.forEach(object => {
            object.selected = false;
            this.splatModel.updateSplatObject(object);
        });

        this.selectedList.length = 0;
    }

    onTap($event: any): void {

        this.clearSelected();

        switch (this.mode) {
            case 'add': {
                this.addSplat(this.splatRenderer.adjustPoint($event.center));
                break;
            }
            case 'remove': {
                this.splatModel.getObjectsUnder(this.splatRenderer.adjustPoint($event.center)).forEach(object => {
                    this.splatModel.removeSplatObject(object);
                });
                break;
            }
            default: {
                this.mode = 'add';
            }
        }
    }

    onPress($event: any): void {

        this.clearSelected();

        this.messageBusService.send(FormPage.CLASS, 'lockSlides');

        this.selectedList = this.splatModel.getObjectsUnder(this.splatRenderer.adjustPoint($event.center));

        this.selectedList.forEach(object => {
            object.selected = true;
            this.splatModel.updateSplatObject(object);
        });

        if (['move', 'rotate'].indexOf(this.mode) === -1) {
            this.mode = 'move';
        }
    }

    onPan($event: any): void {

        const p: Point = this.splatRenderer.adjustPoint($event.center);

        switch (this.mode) {
            case 'move': {
                this.selectedList.forEach(object => {
                    object.center = p;
                    this.splatModel.updateSplatObject(object);
                });
                break;
            }
            case 'rotate': {
                this.selectedList.forEach(object => {
                    object.rotation = $event.deltaY * 2;
                    this.splatModel.updateSplatObject(object);
                });
                break;
            }
            default:
                console.warn('invalid mode');
        }
    }

    onSwipe($event: any): void {
        // no opp
    }

    onRotate($event: any): void {
        // no opp
    }

    setImage(image: string): void {
        this.image = image;
        this.splatModel.setBackground(image);
    }

    getImage(): string {
        return this.image;
    }

    setIcon(icon: string): void {
        this.icon = icon;
        this.mode = 'add';
    }

    getIcon(): string {
        return this.icon;
    }

    protected getSplatImageMap(): SplatImageMap {

        const sim: SplatImageMap = new SplatImageMap();
        const list: any = AppConfig.SPLAT_IMAGE_MAP;

        Object.keys(list).forEach(key => {
            sim.add(key, list[key]);
        });
        return sim;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.splatModel.destroy();
        this.splatRenderer.destroy();
    }

    showTools($event: any): void {
        this.popoverCtrl.create(SplatToolsComponent, {
            splatModel: this.splatModel,
            splatComponent: this,
            iconSize: this.definition.config.iconSize
        }).present({
            ev: $event
        });
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/splat/form/splat-control.component.ts